import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/app/components/ui/carousel';

export interface CarouselHazard {
  id: number;
  step_number: number;
  order?: number;
  task_description: string;
  hazard_description: string;
  risk_level: string;
  control_measures: string;
}

export interface HazardAcknowledgement {
  hazard_id: number;
  viewed_at: string;
}

export interface HazardAcknowledgementProgress {
  acknowledged: number;
  total: number;
}

interface JSAHazardCarouselProps {
  hazards: CarouselHazard[];
  onComplete?: (acknowledgements: HazardAcknowledgement[]) => void;
  /** Fires whenever the number of hazards that have met the dwell requirement changes. */
  onProgress?: (progress: HazardAcknowledgementProgress) => void;
}

const DWELL_MS = 1500;

const riskColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export function JSAHazardCarousel({ hazards, onComplete, onProgress }: JSAHazardCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const [, setTick] = useState(0);
  const viewedRef = useRef<Set<number>>(new Set());
  const viewedAtRef = useRef<Record<number, string>>({});
  const completedRef = useRef(false);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markAtRef = useRef<Record<number, number>>({});
  const mountTimeRef = useRef<string>(new Date().toISOString());
  const lastHazardKeyRef = useRef<string>('');
  const apiRef = useRef<CarouselApi | undefined>(undefined);
  apiRef.current = api;

  const bump = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (hazards.length === 0 && onComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete([]);
    }
  }, [hazards, onComplete]);

  const sortedHazards = useMemo(
    () =>
      [...hazards].sort((a, b) => {
        const aPos = a.order ?? a.step_number;
        const bPos = b.order ?? b.step_number;
        return aPos - bPos || a.id - b.id;
      }),
    [hazards]
  );

  const hazardKey = useMemo(
    () => sortedHazards.map((h) => `${h.id}`).join(','),
    [sortedHazards]
  );

  const markHazardViewed = useCallback(
    (hazard: CarouselHazard) => {
      if (viewedRef.current.has(hazard.id)) return;

      const now = Date.now();
      const enteredAt = markAtRef.current[hazard.id] ?? now;
      const minTime = new Date(mountTimeRef.current).getTime();
      // Sanity-bound viewed_at between the carousel mount time and the current
      // time; it cannot pre-date the session or be in the future.
      const clampedTime = Math.max(minTime, Math.min(now, enteredAt + DWELL_MS));
      const clampedIso = new Date(clampedTime).toISOString();

      const nextViewed = new Set(viewedRef.current);
      nextViewed.add(hazard.id);
      viewedRef.current = nextViewed;
      viewedAtRef.current = { ...viewedAtRef.current, [hazard.id]: clampedIso };
      bump();
    },
    [bump]
  );

  // Reset acknowledgement state whenever the hazard set changes (add, remove,
  // reorder) so a signer cannot reuse stale carousel progress.
  useEffect(() => {
    if (hazardKey === lastHazardKeyRef.current) return;

    const previousKey = lastHazardKeyRef.current;
    lastHazardKeyRef.current = hazardKey;

    viewedRef.current = new Set();
    viewedAtRef.current = {};
    completedRef.current = false;
    markAtRef.current = {};
    mountTimeRef.current = new Date().toISOString();

    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }

    // Reset the carousel back to the first hazard so acknowledgement restarts.
    if (apiRef.current) {
      try {
        apiRef.current.scrollTo(0);
      } catch {
        // scrollTo may not be available on all carousel implementations; ignore.
      }
    }

    // Notify the parent that acknowledgement is incomplete so it can disable
    // signing until the new hazards have been viewed.
    if (previousKey && hazardKey && onComplete) {
      onComplete([]);
    }

    bump();
  }, [hazardKey, bump, onComplete]);

  const clearDwell = useCallback(() => {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!api || sortedHazards.length === 0) return;

    const handleSelect = () => {
      const index = api.selectedScrollSnap();
      setCurrent(index);
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());

      const hazard = sortedHazards[index];
      if (!hazard) return;

      clearDwell();

      if (viewedRef.current.has(hazard.id)) return;

      markAtRef.current = { ...markAtRef.current, [hazard.id]: Date.now() };
      dwellTimerRef.current = setTimeout(() => {
        if (apiRef.current?.selectedScrollSnap() === index) {
          markHazardViewed(hazard);
        }
      }, DWELL_MS);
    };

    handleSelect();
    api.on('select', handleSelect);
    api.on('reInit', handleSelect);

    return () => {
      clearDwell();
      api.off('select', handleSelect);
      api.off('reInit', handleSelect);
    };
  }, [api, sortedHazards, markHazardViewed, clearDwell]);

  const acknowledgedCount = sortedHazards.filter((h) => viewedRef.current.has(h.id)).length;
  const isComplete = sortedHazards.length > 0 && acknowledgedCount === sortedHazards.length;

  useEffect(() => {
    onProgress?.({ acknowledged: acknowledgedCount, total: sortedHazards.length });
  }, [acknowledgedCount, sortedHazards.length, onProgress]);

  useEffect(() => {
    if (isComplete && onComplete && !completedRef.current) {
      completedRef.current = true;

      const now = new Date();
      const minTime = new Date(mountTimeRef.current);
      const acknowledgements = sortedHazards.map((h) => {
        const rawAt = viewedAtRef.current[h.id] || now.toISOString();
        let viewedTime = new Date(rawAt);
        if (isNaN(viewedTime.getTime()) || viewedTime > now) {
          viewedTime = now;
        } else if (viewedTime < minTime) {
          viewedTime = minTime;
        }
        return {
          hazard_id: h.id,
          viewed_at: viewedTime.toISOString(),
        };
      });

      onComplete(acknowledgements);
    }
  }, [isComplete, onComplete, sortedHazards]);

  if (sortedHazards.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-gray-500">
        No hazards documented.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Hazard {current + 1} of {sortedHazards.length}
        </span>
        <div className="h-2 w-32 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${((current + 1) / sortedHazards.length) * 100}%` }}
          />
        </div>
      </div>

      <Carousel setApi={setApi} className="w-full">
        <CarouselContent>
          {sortedHazards.map((h) => (
            <CarouselItem key={h.id}>
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Step {h.step_number}</span>
                    <Badge
                      className={`text-xs capitalize ${riskColors[h.risk_level] || 'bg-gray-100 text-gray-700'}`}
                    >
                      {h.risk_level}
                    </Badge>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Task</h4>
                    <p className="text-sm text-gray-700">{h.task_description}</p>
                  </div>

                  <div className="rounded-md bg-red-50 p-3 border border-red-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-red-800">Danger</h4>
                        <p className="text-sm text-red-700">{h.hazard_description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md bg-green-50 p-3 border border-green-100">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-green-800">Mitigation / Control</h4>
                        <p className="text-sm text-green-800">{h.control_measures}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => api?.scrollPrev()}
          disabled={!canScrollPrev}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => api?.scrollNext()}
          disabled={!canScrollNext}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {isComplete && (
        <div className="text-center text-sm font-medium text-green-700">
          All hazards acknowledged — you can now sign.
        </div>
      )}
    </div>
  );
}
